'use client';

// ESQUEMA de la decisión "Investigación disciplinaria (Art. 211-213)".
// Campos de apertura + prórrogas (Art. 213) + versión libre + pruebas + recursos +
// abogado de oficio. Al marcar una prórroga se genera el "Auto que prorroga el término"
// y, en versión libre, su diligencia (diálogo genérico). La SELECCIÓN (respuestas + PDFs
// + fechas) y el TEXTO editado de cada documento se PERSISTEN en
// `DecisionInicial.documento_datos`, para que el abogado/Director vean exactamente eso.
// Se irá completando poco a poco.

import { useState } from 'react';
import { FolderOpen, FlaskConical, FileText } from 'lucide-react';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import { guardarDocumentoDecision, type DocumentoBorrador } from '@/lib/decisionFlujo';
import type { DatosDoc } from '@/lib/plantillas/decisiones-docs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import CampoPDF from '../CampoPDF';
import { SiNo, AlertaTermino, Campo, SubForm } from './decisionesUI';
import DocumentoDecisionDialog from './DocumentoDecisionDialog';
import { DOC_AUTO_PRORROGA, DOC_VERSION_LIBRE } from '@/lib/plantillas/decisiones-docs';

// Todas las respuestas del formulario (se persisten en documento_datos.seleccion).
interface InvSel {
  fecha?: string;
  prorroga1?: boolean | null;
  prorroga2?: boolean | null;
  fechaNotif?: string;
  notificacionKey?: string;
  versionLibre?: boolean | null;
  versionLibreKey?: string;
  tipoPrueba?: string;
  // Auto de prueba (de oficio o a solicitud)
  autoPruebaOrigen?: string;
  autoPruebaKey?: string;
  autoPruebaFecha?: string;
  // Recursos
  recursos?: boolean | null;
  recursosKey?: string;
  recursosFecha?: string;
  recursosDecision?: string;
  // Solicitud de abogado de oficio
  abogadoOficio?: boolean | null;
  abogadoOficioKey?: string;
  abogadoOficioFecha?: string;
  personeriaKey?: string;
  // Nulidades
  nulidades?: boolean | null;
  nulidadesKey?: string;
  nulidadesFecha?: string;
  nulidadesDecision?: string;
}

export default function InvestigacionForm({ exp }: { exp: ExpDetalle }) {
  const radicado = exp.radicado_manual || exp.radicado;
  const borr = (exp.decision_inicial?.documento_datos ?? {}) as DocumentoBorrador;

  const [sel, setSel] = useState<InvSel>((borr.seleccion as InvSel | undefined) ?? {});
  const [docsData, setDocsData] = useState<Record<string, DatosDoc>>(
    (borr.docs as Record<string, DatosDoc> | undefined) ?? {},
  );
  const [docProrroga, setDocProrroga] = useState(false);
  const [docVersionLibre, setDocVersionLibre] = useState(false);

  // Actualiza una o varias respuestas y las persiste (para el revisor/Director).
  function upd(patch: Partial<InvSel>) {
    const next = { ...sel, ...patch };
    setSel(next);
    void guardarDocumentoDecision(exp.id, { seleccion: next as Record<string, unknown>, docs: docsData }).catch(() => {});
  }
  const guardarDocDe = (id: string) => async (datos: DatosDoc) => {
    const nextDocs = { ...docsData, [id]: datos };
    setDocsData(nextDocs);
    await guardarDocumentoDecision(exp.id, { seleccion: sel as Record<string, unknown>, docs: nextDocs });
  };

  const { prorroga1, prorroga2 } = sel;
  const hayProrroga = prorroga1 === true || prorroga2 === true;
  // Término total (descriptivo): 6 meses + 6 (prórroga 1) + 3 (prórroga 2).
  const meses = 6 + (prorroga1 ? 6 : 0) + (prorroga1 && prorroga2 ? 3 : 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo label="Radicado" requerido>
          <Input value={radicado} readOnly disabled />
        </Campo>
        <Campo label="Fecha de la investigación disciplinaria" requerido>
          <Input type="date" value={sel.fecha ?? ''} onChange={(e) => upd({ fecha: e.target.value })} />
        </Campo>
      </div>

      <AlertaTermino tono={meses > 6 ? 'rojo' : 'ambar'}>
        <strong>
          Alerta de {meses === 6 ? '6 meses' : `${meses} meses (6${prorroga1 ? ' + 6 prórroga' : ''}${prorroga1 && prorroga2 ? ' + 3 prórroga 2' : ''})`} para investigar
        </strong>. <em className="text-muted-foreground">(El vencimiento se calcula con el motor de términos.)</em>
      </AlertaTermino>

      <SiNo
        label="¿Son 2 o más los investigados, o 2 o más las conductas? (se puede prorrogar 6 meses más)"
        value={prorroga1 ?? null} onChange={(v) => upd({ prorroga1: v })}
      >
        <AlertaTermino tono="ambar">Se prorroga <strong>6 meses más</strong> el término para investigar.</AlertaTermino>
      </SiNo>

      <SiNo label="Prórroga 2 (inciso 3, art. 213)" value={prorroga2 ?? null} onChange={(v) => upd({ prorroga2: v })}>
        <AlertaTermino tono="ambar">
          Prórroga de <strong>3 meses</strong> contados <strong>desde la fecha del auto de prórroga</strong>.
        </AlertaTermino>
      </SiNo>

      {/* Documento: Auto que prorroga el término (aparece al marcar cualquier prórroga). */}
      {hayProrroga && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">
              <FileText className="mr-1 inline size-4 text-primary" />
              Documento: <strong>Auto que prorroga la investigación (Art. 213)</strong>
            </span>
            <Button type="button" size="sm" onClick={() => setDocProrroga(true)}>
              <FileText className="size-3.5" /> Generar / previsualizar
            </Button>
          </div>
        </div>
      )}

      <Campo label="Fecha de notificación del auto">
        <Input type="date" value={sel.fechaNotif ?? ''} onChange={(e) => upd({ fechaNotif: e.target.value })} />
      </Campo>
      <CampoPDF carpeta="INVESTIGACION" label="PDF de la notificación" value={sel.notificacionKey} onChange={(p) => upd({ notificacionKey: p })} />

      <SiNo label="Versión libre" value={sel.versionLibre ?? null} onChange={(v) => upd({ versionLibre: v })}>
        {/* Diligencia de versión libre: se genera su documento y se adjunta el PDF firmado. */}
        <div className="space-y-3">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">
                <FileText className="mr-1 inline size-4 text-primary" />
                Documento: <strong>Versión libre (num. 3, art. 112)</strong>
              </span>
              <Button type="button" size="sm" onClick={() => setDocVersionLibre(true)}>
                <FileText className="size-3.5" /> Generar / previsualizar
              </Button>
            </div>
          </div>
          <CampoPDF carpeta="INVESTIGACION" label="PDF de la versión libre" value={sel.versionLibreKey} onChange={(p) => upd({ versionLibreKey: p })} />
        </div>
      </SiNo>

      <SubForm titulo="Pruebas (art. 147)">
        <Campo label="Tipo de prueba (en blanco para diligenciar)">
          <Input value={sel.tipoPrueba ?? ''} onChange={(e) => setSel((s) => ({ ...s, tipoPrueba: e.target.value }))}
            onBlur={() => upd({ tipoPrueba: sel.tipoPrueba })}
            placeholder="Ej.: testimonial, documental, pericial…" />
        </Campo>
      </SubForm>

      {/* Auto de prueba (de oficio o a solicitud): PDF + fecha */}
      <SubForm titulo="Auto de prueba (de oficio o a solicitud)">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo label="Origen">
            <Select value={sel.autoPruebaOrigen || undefined} onValueChange={(v) => upd({ autoPruebaOrigen: v })}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="OFICIO">De oficio</SelectItem>
                <SelectItem value="SOLICITUD">A solicitud</SelectItem>
              </SelectContent>
            </Select>
          </Campo>
          <Campo label="Fecha">
            <Input type="date" value={sel.autoPruebaFecha ?? ''} onChange={(e) => upd({ autoPruebaFecha: e.target.value })} />
          </Campo>
        </div>
        <CampoPDF carpeta="INVESTIGACION" label="PDF del auto de prueba" value={sel.autoPruebaKey} onChange={(p) => upd({ autoPruebaKey: p })} />
      </SubForm>

      {/* Recursos: Sí/No → PDF + fecha + decisión */}
      <SiNo label="Recursos" value={sel.recursos ?? null} onChange={(v) => upd({ recursos: v })}>
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Fecha">
              <Input type="date" value={sel.recursosFecha ?? ''} onChange={(e) => upd({ recursosFecha: e.target.value })} />
            </Campo>
            <Campo label="Decisión">
              <Input value={sel.recursosDecision ?? ''} onChange={(e) => setSel((s) => ({ ...s, recursosDecision: e.target.value }))}
                onBlur={() => upd({ recursosDecision: sel.recursosDecision })}
                placeholder="Decisión sobre el recurso" />
            </Campo>
          </div>
          <CampoPDF carpeta="INVESTIGACION" label="PDF del recurso" value={sel.recursosKey} onChange={(p) => upd({ recursosKey: p })} />
        </div>
      </SiNo>

      {/* Solicitud de abogado de oficio: Sí/No → PDF solicitud + fecha // reconocimiento de personería jurídica */}
      <SiNo label="Solicitud de abogado de oficio" value={sel.abogadoOficio ?? null} onChange={(v) => upd({ abogadoOficio: v })}>
        <div className="space-y-3">
          <Campo label="Fecha de la solicitud">
            <Input type="date" value={sel.abogadoOficioFecha ?? ''} onChange={(e) => upd({ abogadoOficioFecha: e.target.value })} />
          </Campo>
          <CampoPDF carpeta="INVESTIGACION" label="PDF de la solicitud" value={sel.abogadoOficioKey} onChange={(p) => upd({ abogadoOficioKey: p })} />
          <SubForm titulo="Asignación · Reconocimiento de personería jurídica del defensor">
            <CampoPDF carpeta="INVESTIGACION" label="PDF de reconocimiento de personería jurídica" value={sel.personeriaKey} onChange={(p) => upd({ personeriaKey: p })} />
          </SubForm>
        </div>
      </SiNo>

      {/* Nulidades: Sí/No → fecha + PDF + decisión */}
      <SiNo label="Nulidades" value={sel.nulidades ?? null} onChange={(v) => upd({ nulidades: v })}>
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Fecha">
              <Input type="date" value={sel.nulidadesFecha ?? ''} onChange={(e) => upd({ nulidadesFecha: e.target.value })} />
            </Campo>
            <Campo label="Decisión">
              <Input value={sel.nulidadesDecision ?? ''} onChange={(e) => setSel((s) => ({ ...s, nulidadesDecision: e.target.value }))}
                onBlur={() => upd({ nulidadesDecision: sel.nulidadesDecision })}
                placeholder="Decisión sobre la nulidad" />
            </Campo>
          </div>
          <CampoPDF carpeta="INVESTIGACION" label="PDF de la nulidad" value={sel.nulidadesKey} onChange={(p) => upd({ nulidadesKey: p })} />
        </div>
      </SiNo>

      <p className="flex items-start gap-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <FlaskConical className="mt-0.5 size-4 shrink-0" />
        El <strong>cierre de la investigación</strong>, la <strong>evaluación (archivo / pliego de cargos)</strong> y la
        <strong> remisión a Juzgamiento</strong> continúan en los pasos de <strong>Instrucción</strong> del expediente.
      </p>
      <p className="flex items-center gap-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <FolderOpen className="size-4" /> Lo que diligencies y guardes queda disponible para el abogado especializado y el Director.
      </p>

      {docProrroga && (
        <DocumentoDecisionDialog
          exp={exp} cfg={DOC_AUTO_PRORROGA}
          guardado={docsData.AUTO_PRORROGA} onGuardar={guardarDocDe('AUTO_PRORROGA')}
          onClose={() => setDocProrroga(false)}
        />
      )}
      {docVersionLibre && (
        <DocumentoDecisionDialog
          exp={exp} cfg={DOC_VERSION_LIBRE}
          guardado={docsData.VERSION_LIBRE} onGuardar={guardarDocDe('VERSION_LIBRE')}
          onClose={() => setDocVersionLibre(false)}
        />
      )}
    </div>
  );
}
